import { useCallback, useEffect, useRef, useState } from "react";
import type { AccountAuthState } from "@/auth/accountTypes";
import { discoverClient, type DiscoverApp, type PublicAppAdmission } from "../discoverClient";
import type { DiscoverySelection } from "./types";
import { launchFailureDetails, type LaunchFailure } from "./launchFailure";

type Status = "loading" | "ready" | "error";
type Launch = {
  failure?: LaunchFailure;
  admission?: PublicAppAdmission | null;
  target: string;
  app: DiscoverApp | null;
  status: Status;
  error: string | null;
};
type Detail = {
  key: string;
  app: DiscoverApp | null;
  status: Status;
  error: string | null;
};

/** Public launch and account detail have separate dependencies and cancellation.
 * A delayed list or account response never becomes a new launch selection.
 */
export function useDiscoverSelection(
  selection: DiscoverySelection | null,
  accountState: AccountAuthState,
  userId: string | null,
  resolveDefault: boolean,
  options: { staticPreviewUrl?: string | null } = {},
) {
  const target =
    selection?.kind === "discover"
      ? selection.id
      : !selection && resolveDefault
        ? ""
        : null;
  const [launch, setLaunch] = useState<Launch | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [revision, setRevision] = useState(0);
  const [launchRevision, setLaunchRevision] = useState(0);
  const retry = useCallback(() => { setLaunch(null); setLaunchRevision((value) => value + 1); }, []);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const edits = useRef(0);
  useEffect(() => {
    if (target === null) {
      // Returning to the default selection must await fresh admission instead
      // of briefly exposing the former, already redeemed launch credential.
      setLaunch(null);
      return;
    }
    // A public static deployment already carries everything Open needs. A
    // /start round-trip would only re-fetch metadata and an admission the
    // visitor does not use; the listing projection has already resolved the
    // verified URL.
    if (options.staticPreviewUrl) {
      setLaunch({ target, app: null, status: "ready", error: null });
      return;
    }
    const controller = new AbortController();
    setLaunch({ target, app: null, status: "loading", error: null });
    void Promise.resolve()
      .then(() =>
        controller.signal.aborted
          ? null
          : discoverClient.launch(target || null, controller.signal),
      )
      .then((result) => {
        if (result && !controller.signal.aborted)
          setLaunch({ target, app: result.app, admission: result.public_app, status: "ready", error: null });
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setLaunch({
            target,
            app: null,
            status: "error",
            failure: launchFailureDetails(cause),
            error:
              cause instanceof Error
                ? cause.message
                : "This app could not load.",
          });
      });
    return () => controller.abort();
  }, [target, launchRevision, options.staticPreviewUrl]);

  const publicApp = launch?.target === target ? launch.app : null;
  const id = publicApp?.id ?? null;
  const version = publicApp?.published_version;
  const scope = accountState.status === "signed_in" ? userId : null;
  const authorized =
    accountState.status === "signed_out" ||
    (accountState.status === "signed_in" && !!userId);
  const detailKey = JSON.stringify([scope, id, version]);
  const activeDetailKey = useRef("");
  activeDetailKey.current = authorized ? detailKey : "";
  useEffect(() => {
    if (!id || !authorized) return;
    const controller = new AbortController();
    const requestedEdits = edits.current;
    setDetail((current) =>
      current?.key === detailKey
        ? { ...current, status: "loading", error: null }
        : { key: detailKey, app: null, status: "loading", error: null },
    );
    void Promise.resolve()
      .then(() =>
        controller.signal.aborted
          ? null
          : discoverClient.detail(id, controller.signal),
      )
      .then((result) => {
        if (!result || controller.signal.aborted) return;
        if (result.app.published_version !== version) {
          // A publication changed during startup. Revalidate public metadata;
          // never promote account-specific detail into the public launch state.
          retry();
          return;
        }
        setDetail((current) => {
          const edited =
            current?.key === detailKey && edits.current !== requestedEdits
              ? current.app
              : null;
          return {
            key: detailKey,
            status: "ready",
            error: null,
            app: edited
              ? {
                  ...result.app,
                  stats: edited.stats,
                  viewer: edited.viewer,
                  added: edited.added,
                  instance_id: edited.instance_id,
                  app_url: edited.app_url,
                }
              : result.app,
          };
        });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          cause instanceof Error
            ? cause.message
            : "App details could not load.";
        setDetail((current) => ({
          key: detailKey,
          app: current?.key === detailKey ? current.app : null,
          status: "error",
          error: message,
        }));
        if (
          cause &&
          typeof cause === "object" &&
          "status" in cause &&
          cause.status === 404
        )
          setLaunch((current) =>
            current?.target === target
              ? { ...current, app: null, status: "error", error: message, failure: launchFailureDetails(cause) }
              : current,
          );
      });
    return () => controller.abort();
  }, [id, version, scope, authorized, detailKey, revision, target, retry]);

  const currentDetail = authorized && detail?.key === detailKey ? detail : null;
  const app =
    publicApp &&
    currentDetail?.app &&
    currentDetail.app.published_version === version
      ? currentDetail.app
      : publicApp;
  const updateApp = useCallback(
    (appId: string, update: (app: DiscoverApp) => DiscoverApp) => {
      if (
        !authorized ||
        publicApp?.id !== appId ||
        activeDetailKey.current !== detailKey
      )
        return;
      edits.current += 1;
      setDetail((current) => ({
        key: detailKey,
        status: "ready",
        error: null,
        app: update(
          current?.key === detailKey && current.app ? current.app : publicApp,
        ),
      }));
    },
    [authorized, detailKey, publicApp],
  );
  return {
    app,
    admission: launch?.target === target ? launch.admission : undefined,
    refresh,
    retry,
    updateApp,
    status: launch?.target === target ? launch.status : ("loading" as const),
    error: launch?.target === target ? launch.error : null,
    failure: launch?.target === target ? launch.failure : undefined,
    detailStatus: currentDetail?.status ?? "loading",
    detailError: currentDetail?.error ?? null,
  };
}
